import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'atr',
  name: 'Average True Range',
  shortName: 'ATR',
  category: 'volatility',
  overlay: false,
  params: [{ key: 'period', label: 'Period', type: 'number', default: 14, min: 1, max: 200 }],
  lines: [{ key: 'atr', label: 'ATR', color: '#00acc1', width: 1 }],
}

class ATR extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const period = Math.max(1, Number(params['period']) || 14)
    const values: (number | null)[] = new Array<number | null>(candles.length).fill(null)

    if (candles.length < 2) return { lines: [{ key: 'atr', values }] }

    // True range for each bar (first bar has no previous close)
    const tr: number[] = [candles[0].h - candles[0].l]
    for (let i = 1; i < candles.length; i++) {
      const hl = candles[i].h - candles[i].l
      const hc = Math.abs(candles[i].h - candles[i - 1].c)
      const lc = Math.abs(candles[i].l - candles[i - 1].c)
      tr.push(Math.max(hl, hc, lc))
    }

    // Seed ATR with simple average of first `period` true ranges
    if (candles.length < period) return { lines: [{ key: 'atr', values }] }

    let atr = 0
    for (let i = 0; i < period; i++) atr += tr[i]
    atr /= period
    values[period - 1] = atr

    // Wilder's smoothing
    for (let i = period; i < candles.length; i++) {
      atr = (atr * (period - 1) + tr[i]) / period
      values[i] = atr
    }

    return { lines: [{ key: 'atr', values }] }
  }
}

registerIndicator(ATR, CONFIG)
